// TEMPORÁRIO (out/2026 — remover em dez/2026 ou jan/2027) junto com a rota
// /importar-flash do backend (ver flash.importar.ts lá).
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api } from "@/lib/api";
import { formatCents } from "@/lib/format";
import { toast } from "sonner";
import { AlertTriangle, FileSpreadsheet, Loader2 } from "lucide-react";

type Acao = "incluir" | "alterar" | "sem_alteracao" | "remover" | "erro";

interface Linha {
  linha: number | null;
  colaboradorId: string | null;
  nome: string;
  acao: Acao;
  totalDe: number;
  totalPara: number;
  erros: string[];
}

interface Resultado {
  confirmado: boolean;
  linhas: Linha[];
  resumo: Record<Acao, number>;
  totais: { de: number; para: number };
}

const ACAO: Record<Acao, { rotulo: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  erro: { rotulo: "Erro", variant: "destructive" },
  remover: { rotulo: "Zera", variant: "destructive" },
  incluir: { rotulo: "Entra", variant: "default" },
  alterar: { rotulo: "Muda", variant: "secondary" },
  sem_alteracao: { rotulo: "Igual", variant: "outline" },
};
const ORDEM: Acao[] = ["erro", "remover", "incluir", "alterar", "sem_alteracao"];

interface Props {
  open: boolean;
  onClose: () => void;
  folhaId: string;
  onSalvo: () => void;
}

/** Substitui a folha inteira pela planilha de pedido da Flash. */
export const ImportarFlashModal = ({ open, onClose, folhaId, onSalvo }: Props) => {
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [previa, setPrevia] = useState<Resultado | null>(null);
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    if (open) return;
    setArquivo(null);
    setPrevia(null);
  }, [open]);

  const enviar = (f: File, confirmar: boolean) => {
    const fd = new FormData();
    fd.append("arquivo", f);
    return api.postForm<Resultado>(
      `/beneficios/folhas/${folhaId}/importar-flash${confirmar ? "?confirmar=true" : ""}`,
      fd,
    );
  };

  const escolher = async (f: File | undefined) => {
    if (!f) return;
    setArquivo(f);
    setPrevia(null);
    setCarregando(true);
    try {
      setPrevia(await enviar(f, false));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao ler a planilha");
      setArquivo(null);
    } finally {
      setCarregando(false);
    }
  };

  const confirmar = async () => {
    if (!arquivo) return;
    setCarregando(true);
    try {
      const r = await enviar(arquivo, true);
      toast.success(`Folha substituída pela planilha da Flash (${formatCents(r.totais.para)})`);
      onSalvo();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao importar");
    } finally {
      setCarregando(false);
    }
  };

  const linhas = [...(previa?.linhas ?? [])].sort(
    (a, b) => ORDEM.indexOf(a.acao) - ORDEM.indexOf(b.acao) || a.nome.localeCompare(b.nome, "pt-BR"),
  );
  const comErro = (previa?.resumo.erro ?? 0) > 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importar planilha da Flash</DialogTitle>
          <DialogDescription>
            Use a planilha de pedido da Flash (CPF e colunas de benefício). A importação{" "}
            <strong className="text-foreground">substitui a folha inteira</strong>: quem não estiver na planilha fica
            sem benefício nesta folha, e coluna vazia vale zero. Os colaboradores são achados pelo CPF.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-sm hover:bg-muted/50">
            <FileSpreadsheet className="h-4 w-4" />
            {arquivo ? arquivo.name : "Selecionar planilha"}
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(e) => {
                void escolher(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
          </label>
          {carregando && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>

        {previa && (
          <>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {ORDEM.filter((a) => previa.resumo[a] > 0).map((a) => (
                <Badge key={a} variant={ACAO[a].variant}>
                  {previa.resumo[a]} {ACAO[a].rotulo.toLowerCase()}
                </Badge>
              ))}
              <span className="ml-auto text-sm tabular-nums text-muted-foreground">
                Total da folha: {formatCents(previa.totais.de)} →{" "}
                <strong className="text-foreground">{formatCents(previa.totais.para)}</strong>
              </span>
            </div>

            {comErro && (
              <p className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                Corrija as linhas com erro e selecione a planilha de novo. Como a importação substitui a folha inteira,
                ela só é liberada sem nenhum erro.
              </p>
            )}

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-14">Linha</TableHead>
                  <TableHead>Colaborador</TableHead>
                  <TableHead className="w-20" />
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {linhas.map((l, i) => (
                  <TableRow key={`${l.linha ?? "x"}-${l.colaboradorId ?? i}`}>
                    <TableCell className="text-muted-foreground">{l.linha ?? "—"}</TableCell>
                    <TableCell>
                      <span className="font-medium">{l.nome || "—"}</span>
                      {l.acao === "erro" && (
                        <span className="block text-xs text-destructive">{l.erros.join(" ")}</span>
                      )}
                      {l.acao === "remover" && l.linha === null && (
                        <span className="block text-xs text-muted-foreground">Não está na planilha</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={ACAO[l.acao].variant}>{ACAO[l.acao].rotulo}</Badge>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right text-sm tabular-nums">
                      {l.acao === "sem_alteracao" ? (
                        <span className="text-muted-foreground">{formatCents(l.totalPara)}</span>
                      ) : (
                        <>
                          {formatCents(l.totalDe)} → <strong>{formatCents(l.totalPara)}</strong>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={carregando}>
            Cancelar
          </Button>
          <Button onClick={() => void confirmar()} disabled={carregando || !previa || comErro}>
            {carregando && previa && <Loader2 className="animate-spin" />}
            Substituir a folha
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
